import cx from "classnames";
import { FC, JSX, ReactNode, useEffect, useId, useMemo, useRef, useState } from "react";
import { DropdownItem, NestingParentDropdownItem } from "@app/shared/checkbox-dropdown/types";
import { CheckboxStateEnum, QESCheckbox } from "@app/shared/qes-standards/forms/Checkbox";
import { expandCollapse } from "@app/shared/qes-standards/utility/expandCollapse";
import { Dictionary, flatMap, groupBy, uniqBy } from "lodash";
import { QesIcon } from "@app/shared/icons/QESIcon";
import { QesIcons } from "@app/shared/icons/qesIcons";
import { QesColors } from "@app/shared/qes-standards/colors";
import { QESButton } from "@app/shared/qes-standards/Button";
import { QESTextInput } from "@app/shared/qes-standards/forms/TextInput";

interface MultiselectDropdownProps {
  /**
   * Optional class name(s) added to the div outside the dropdown
   */
  containerClassName?: string;
  /**
   * A string to be rendered next to the Select All button.<br/>
   * This defaults to "Select All"
   */
  customSelectAllText?: string;
  /**
   * A string to be rendered inside the text input/dropdown button.<br/>
   * This defaults to "x item(s) selected," which reflects the children selected only
   */
  customSelectInputText?: string;
  /**
   * Disables the text field, dropdown button, and all inputs and checkboxes inside the dropdown.<br/>
   * Does not disable the accordions within the dropdown.
   */
  disabled?: boolean;
  /**
   * When true, search only matches child item names (original behavior).
   * When false or omitted, search also matches parent group names.
   */
  disableParentSearch?: boolean;
  /**
   * There are duplicate checkbox entrees, due to them appearing in multiple categories
   */
  hasDuplicates?: boolean;
  /**
   * Visually hides the label above the dropdown
   */
  hideLabel?: boolean;
  /**
   * Removes the checkbox for each parent item
   */
  hideParentCheckboxes?: boolean;
  /**
   * Hides the search field within the dropdown
   */
  hideSearch?: boolean;
  /**
   * Hides the bulk "select all" checkbox within the dropdown
   */
  hideSelectAll?: boolean;
  /**
   * Hides the grandparent row's leading "+"/"-" expander so it uses the same chevron as parent rows.
   * Defaults to showing "+"/"-" (existing consumers unaffected).
   */
  hideGrandparentExpanderIcon?: boolean;
  /**
   * Always render the grandparent tier, even with a single `grandparentTitle` group (which otherwise
   * flattens, e.g. when a search narrows to one grandparent). Defaults off (existing consumers unaffected).
   */
  alwaysGroupByGrandparentTitle?: boolean;
  /**
   * The list of items rendered within the dropdown
   */
  items: NestingParentDropdownItem[];
  /**
   * Keeps the parent open at all times and disables the chevron icon and closing.
   */
  keepParentOpen?: boolean;
  /**
   * The label of the text element outside the dropdown
   */
  label: string | JSX.Element;
  /**
   * Sets a limit on the number of boxes that can be checked before all the others are disabled.
   * This component doesn't really have a concept of "checked parents," only children, so checking a parent
   * counts as checking all of its children.
   */
  maxSelectedChildren?: number;
  /**
   * Called when the dropdown closes (transitions from open to closed).
   */
  onClose?: () => void;
  /**
   * An optional function called on checkbox li mouseover
   */
  onDropdownItemMouseOver?: (dropdownItemKey?: string) => void;
  /**
   * An optional function called on parent accordion mouseover
   */
  onParentMouseOver?: (parentKey?: string) => void;
  /**
   * A function called on every checkbox change, returning an array of the keys of items currently selected
   */
  onSelectionChanged: (keys: string[]) => void;
  /**
   * Allows for the parent to be selected as a value when it has no children.
   */
  parentsAsValues?: boolean;
  /**
   * An array of strings matching the keys of selected children items
   */
  selectedItems: string[];
  /**
   * Display label text in tooltip if text overflows container.
   */
  showTooltipOnOverflow?: boolean;
  /**
   * Required, static, unique string to help QA Automation run tests involving this component
   */
  testId: string;
  /**
   * An optional string that appears in a tooltip to the right of the label.<br/>
   * This is intended to display validation requirements to the user.
   */
  validationTooltipContent?: string | JSX.Element;
  /**
   * Optional pill-bar filters rendered at the top of the open dropdown panel.
   * Rendered in the supplied order. The dropdown owns the pill UI and toggles
   * its visual state; the consumer supplies a `matchFilter` predicate to tell
   * the dropdown which children correspond to which filter keys.
   */
  filters?: { key: string; displayValue: string }[];
  /**
   * Keys of the currently active pills. Required when `filters` is provided.
   */
  selectedFilters?: string[];
  /**
   * Called with the next selected-keys array when a pill is toggled.
   */
  onSelectedFiltersChange?: (keys: string[]) => void;
  /**
   * Consumer predicate: when `selectedFilters.length > 0` and this returns
   * false, the child is hidden, and parents with no matching children are
   * hidden as well. When `selectedFilters` is empty or omitted, the
   * predicate is not invoked and all items are shown.
   */
  matchFilter?: (child: DropdownItem<string>, activeKeys: string[]) => boolean;
}

/**
 * A parent's selectable children — divider rows are presentational, so they stay out of selection state, counts,
 * and Select All.
 */
const selectableChildren = (parent: NestingParentDropdownItem): DropdownItem<string>[] =>
  parent.children.filter((child) => !child.isDivider);

/**
 * A button that summons a dropdown. The dropdown includes
 * a search field for its contents, a bulk select all checkbox, and a line item 
 * for each item passed in through the items prop. A change event handler is called on 
 * every checkbox click, so there's no need for a submit button. <br /><br />
 * The parents from the items prop are rendered as closed accordions, which can be opened on click. 
 * The children of each parent are rendered when the accordion is opened. By default, the search box
 * matches against both parent and child names. Set disableParentSearch to revert to child-only matching.
 * Only the state of children checkboxes is tracked. Parent checkboxes are
 * calculated based on their children, and serve as select all boxes for their own children.
 * <br /><br />
 * Storybook note: The dotted rectangle is the example's document body, or the area 
 * a user can click in to hide the dropdown.
 */
const MultiselectNestedDropdown = (props: MultiselectDropdownProps & { children?: ReactNode }): JSX.Element => {
  // Apply pill-filter predicate to children. When matchFilter is provided and
  // at least one pill is active, non-matching children are hidden and parents
  // with no matching children are hidden as well. Hidden items' selection
  // state in `props.selectedItems` is left untouched.
  const effectiveItems = useMemo<NestingParentDropdownItem[]>(() => {
    const activeKeys = props.selectedFilters ?? [];
    if (!props.matchFilter || activeKeys.length === 0) {
      return props.items;
    }
    return props.items.reduce<NestingParentDropdownItem[]>((acc, parent) => {
      if (parent.children.length === 0) {
        acc.push(parent);
        return acc;
      }
      const matchingChildren = parent.children.filter((child) => props.matchFilter(child, activeKeys));
      if (matchingChildren.length > 0) {
        acc.push({ ...parent, children: matchingChildren });
      }
      return acc;
    }, []);
  }, [props.items, props.selectedFilters, props.matchFilter]);

  const [isOpen, setIsOpen] = useState(false);
  const [selectInputText, setSelectInputText] = useState("");
  const [searchText, setSearchText] = useState("");
  const [visibleItems, setVisibleItems] = useState(effectiveItems);
  const [parentMatchKeys, setParentMatchKeys] = useState<Set<string>>(new Set());
  const [selectedLookup, setSelectedLookup] = useState<Record<string, boolean>>({});
  const hasBeenOpened = useRef(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const isBulkCheckboxChecked = useRef(false);
  const isBulkCheckboxIndeterminate = useRef(false);
  const uniqueId = useId();

  // gets the selected child count
  // but also updates the bulk checkbox so we don't have to iterate again
  const selectedChildCount = useMemo(() => {
    let selectedCount = 0;
    let totalCount = 0;
    effectiveItems.forEach((parent) => {
      if (props.parentsAsValues && parent.children.length === 0) {
        if (props.selectedItems.includes(parent.key)) {
          selectedCount += 1;
        }
        totalCount += 1;
        return;
      }

      if (!props.hasDuplicates) {
        selectableChildren(parent).forEach((child) => {
          if (props.selectedItems.includes(child.key)) {
            selectedCount += 1;
          }
          totalCount += 1;
        })
      }
    })

    // account for items with duplicate entries differently, so that they only get counted once
    if (props.hasDuplicates) {
      const uniqueItems: DropdownItem<string>[] = uniqBy(flatMap(effectiveItems.map(x => selectableChildren(x))), "key");

      uniqueItems.forEach((child) => {
        if (props.selectedItems.includes(child.key)) {
          selectedCount += 1;
        }
        totalCount += 1;
      })
    }

    if (selectedCount >= totalCount) {
      isBulkCheckboxChecked.current = true;
      isBulkCheckboxIndeterminate.current = false;
    } else if (selectedCount > 0) {
      isBulkCheckboxChecked.current = false;
      isBulkCheckboxIndeterminate.current = true;
    } else {
      isBulkCheckboxChecked.current = false;
      isBulkCheckboxIndeterminate.current = false;
    }
    return selectedCount;
  }, [effectiveItems, props.selectedItems]);

  // when items are updated, refresh the selected items lookup
  useEffect(() => {
    const newSelectedLookup: Record<string, boolean> = {};
    effectiveItems.forEach((parent) => {
      if (props.parentsAsValues && parent.children.length === 0) {
        newSelectedLookup[parent.key] = props.selectedItems.includes(parent.key);
      } else {
        selectableChildren(parent).forEach((child) => {
          newSelectedLookup[child.key] = props.selectedItems.includes(child.key);
        });
      }
    })
    setSelectedLookup(newSelectedLookup);
  }, [effectiveItems, props.selectedItems]);

  useEffect(() => {
    if (isOpen) {
      hasBeenOpened.current = true;
    } else if (hasBeenOpened.current) {
      props.onClose?.();
      setSearchText("");
    }
  }, [isOpen]);

  // Update the dropdown text on selection change
  useEffect(() => {
    if (!props.customSelectInputText) {
      if (selectedChildCount > 0) {
        setSelectInputText(`${selectedChildCount} item(s) selected`);
      } else {
        setSelectInputText("");
      }
    }
  }, [effectiveItems, props.selectedItems]);

  // filter items by search text
  useEffect(() => {
    const newVisibleItems: NestingParentDropdownItem[] = [];
    const newParentMatchKeys = new Set<string>();
    const lowerSearch = searchText.toLowerCase();

    effectiveItems.forEach((parent) => {
      if (props.parentsAsValues && parent.children.length === 0 && parent.name.toLowerCase().includes(lowerSearch)) {
        newVisibleItems.push(parent);
        return;
      }

      const parentNameMatches = !props.disableParentSearch
        && searchText.length > 0
        && !parent.hideParent
        && parent.name.toLowerCase().includes(lowerSearch);

      if (parentNameMatches) {
        newParentMatchKeys.add(parent.key);
        newVisibleItems.push(parent);
        return;
      }

      // Dividers caption a section rather than match one, so a search drops them instead of leaving a stray separator.
      const searchableChildren = searchText.length > 0 ? selectableChildren(parent) : parent.children;
      const itemsToInclude = searchableChildren.filter((child) => child.name.toLowerCase().includes(lowerSearch));
      if (itemsToInclude.length > 0) {
        newVisibleItems.push({
          ...parent,
          children: itemsToInclude,
        })
      }
    })
    setVisibleItems(newVisibleItems);
    setParentMatchKeys(newParentMatchKeys);
  }, [effectiveItems, searchText, props.disableParentSearch]);

  // close the dropdown if the user clicks outside it
  useEffect(() => {
    if (isOpen) {
      const handleExternalClick = (e: MouseEvent) => {
        const target = e.target as HTMLElement;
        if (target.closest(`#${CSS.escape(uniqueId)}.multiselect-dropdown-container`) === null && isOpen) {
          setIsOpen(false);
          document.body.removeEventListener("click", handleExternalClick);
        }
      };
      document.body.addEventListener("click", handleExternalClick);

      return () => {
        document.body.removeEventListener("click", handleExternalClick);
      };
    }
  }, [isOpen]);

  const updateSelectedItems = (parentKey: string, childKey: string, isChecked: boolean) => {
    const newSelectedItems = new Set(props.selectedItems);
    const newSelectedLookup: Record<string, boolean> = {};
    const parent = effectiveItems.find((parent) => parent.key === parentKey);
    if (!!childKey) {
      const child = parent.children.find((child) => child.key === childKey);
      if (isChecked) {
        newSelectedItems.add(child.key);
        newSelectedLookup[child.key] = true;
      } else {
        newSelectedItems.delete(child.key);
        newSelectedLookup[child.key] = false;
      }
    } else if (props.parentsAsValues && parent.children.length === 0) {
      // if the parent has no children, we want to add it as a value
      if (isChecked) {
        newSelectedItems.add(parent.key);
        newSelectedLookup[parent.key] = true;
      } else {
        newSelectedItems.delete(parent.key);
        newSelectedLookup[parent.key] = false;
      }
    }
    else {
      // only change visible items
      const visibleChildrenKeys = selectableChildren(visibleItems.find((vp) => vp.key === parent.key)).map((child) => child.key);
      selectableChildren(parent).forEach((child) => {
        if (visibleChildrenKeys.includes(child.key)) {
          if (isChecked) {
            newSelectedItems.add(child.key);
            newSelectedLookup[child.key] = true;
          } else {
            newSelectedItems.delete(child.key);
            newSelectedLookup[child.key] = false;
          }
        }
      })
    }

    // don't really need to set selected lookup since it'll get refreshed when the new selectedItems prop comes down
    // and the double update tends to cause a little flicker
    // setSelectedLookup(newSelectedLookup);
    props.onSelectionChanged([...newSelectedItems]);
  };

  const toggleSelectAll = () => {
    const newSelectedItems = new Set(props.selectedItems);
    const newSelectedLookup: Record<string, boolean> = {};
    effectiveItems.forEach((parent) => {
      if (props.parentsAsValues && parent.children.length === 0) {
        if (!isBulkCheckboxChecked.current) {
          newSelectedItems.add(parent.key);
          newSelectedLookup[parent.key] = true;
        } else {
          newSelectedItems.delete(parent.key);
          newSelectedLookup[parent.key] = false;
        }
        return;
      }

      // if a parent has no visible children it won't be in visibleItems
      const visibleParent = visibleItems.find((vp) => vp.key === parent.key);
      if (!!visibleParent) {
        const visibleChildrenKeys = selectableChildren(visibleParent).map((child) => child.key);
        selectableChildren(parent).forEach((child) => {
          if (visibleChildrenKeys.includes(child.key)) {
            if (!isBulkCheckboxChecked.current) {
              newSelectedItems.add(child.key);
              newSelectedLookup[child.key] = true;
            } else {
              newSelectedItems.delete(child.key);
              newSelectedLookup[child.key] = false;
            }
          }
        })
      }
    })

    props.onSelectionChanged([...newSelectedItems]);
  }

  const groupedByGrandparentTitle = () => {
    const grouped: Dictionary<NestingParentDropdownItem[]> = groupBy(visibleItems, "grandparentTitle");

    if (Object.entries(grouped).length > 1 || props.alwaysGroupByGrandparentTitle) {
      return (
        Object.entries(grouped).map((datum, index) => (
          <Grandparent
            disabled={props.disabled}
            grandparentTitle={datum[0]}
            grouped={datum[1]}
            hideExpanderIcon={props.hideGrandparentExpanderIcon}
            hideParentCheckbox={props.hideParentCheckboxes}
            key={index}
            updateSelectedItems={updateSelectedItems}
            selectedLookup={selectedLookup}
            showTooltipOnOverflow={props.showTooltipOnOverflow}
            onDropdownItemMouseOver={props.onDropdownItemMouseOver}
            onParentMouseOver={props.onParentMouseOver}
            parentAsValue={props.parentsAsValues}
            parentMatchKeys={parentMatchKeys}
            keepParentOpen={props.keepParentOpen}
            maxSelectedChildren={props.maxSelectedChildren}
            searchText={searchText}
          />
        ))
      );
    } else {
      return (
        <ul key={`${uniqueId}`}>
          {visibleItems.map((parent) => {
            return (
              <li key={parent.key}>
                <ParentAndChildren
                  disabled={props.disabled}
                  hideParentCheckbox={props.hideParentCheckboxes}
                  parent={parent}
                  parentMatchesSearch={parentMatchKeys.has(parent.key)}
                  updateSelectedItems={updateSelectedItems}
                  searchText={searchText}
                  selectedLookup={selectedLookup}
                  showTooltipOnOverflow={props.showTooltipOnOverflow}
                  onDropdownItemMouseOver={props.onDropdownItemMouseOver}
                  onParentMouseOver={props.onParentMouseOver}
                  parentAsValue={props.parentsAsValues}
                  keepParentOpen={props.keepParentOpen}
                  maxSelectedChildren={props.maxSelectedChildren}
                />
              </li>
            )
          })}
        </ul>
      )
    }
  }

  return (
    <div className={cx({
      [props.containerClassName]: !!props.containerClassName,
      "d-inline-block": true,
      "multiselect-dropdown-container multiselect-nested-dropdown": true,
      "position-relative": true,
    })}
      id={uniqueId}
    >
      <QESTextInput
        containerClassName="select-input"
        disabled={props.disabled}
        hideLabel={props.hideLabel}
        qesRef={inputRef}
        inputAppend={
          <QESButton
            qesType="secondary"
            disabled={props.disabled}
            qesTestId={`multiselect-nested-dropdown__toggle-button--${props.testId}`}
            onMouseDown={(e) => { e.preventDefault(); inputRef.current.focus(); inputRef.current.click(); }}
            className="select-button"
            aria-label="Toggle Dropdown"
          >
            <div className="multiselect-dropdown-icon"><QesIcon iconName={QesIcons.ChevronDown} description="Expand" width="16" color={QesColors.QesPrimaryGray8} /></div>
          </QESButton>
        }

        label={props.label}
        onClick={() => setIsOpen(!isOpen)}
        readOnly={true}
        testId={`search-checkbox-dropdown__${props.testId}-select-input`}
        validationTooltipContent={props.validationTooltipContent}
        value={!!props.customSelectInputText ? props.customSelectInputText : selectInputText}
      />
      {isOpen && (
        <div
          className={cx({
            "checkbox-dropdown": true,
          })}
        >
          {props.filters && props.filters.length > 0 && (
            <div className="multiselect-nested-dropdown__filter-pill-bar">
              <span className="multiselect-nested-dropdown__filter-pill-bar-label">
                Filter by:
              </span>
              {props.filters.map((filter) => {
                const isActive = props.selectedFilters?.includes(filter.key) ?? false;
                return (
                  <QESButton
                    key={filter.key}
                    qesTestId={`${props.testId}__filter-pill--${filter.key}`}
                    qesType="none"
                    aria-pressed={isActive}
                    onClick={(e) => {
                      e.stopPropagation();
                      const current = props.selectedFilters ?? [];
                      const next = isActive
                        ? current.filter((k) => k !== filter.key)
                        : [...current, filter.key];
                      props.onSelectedFiltersChange?.(next);
                    }}
                    className={`multiselect-nested-dropdown__filter-pill${isActive ? " multiselect-nested-dropdown__filter-pill--active" : ""}`}
                  >
                    {filter.displayValue}
                  </QESButton>
                );
              })}
            </div>
          )}
          {!props.hideSearch ?
            <QESTextInput
              testId={`search-checkbox-dropdown__${props.testId}-search-input`}
              disabled={props.disabled}
              hideLabel={true}
              label=""
              placeholder="Search..."
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              containerClassName="checkbox-dropdown__label"
            /> : null
          }
          {!props.hideSelectAll &&
            <QESCheckbox
              checked={isBulkCheckboxChecked.current}
              className="checkbox-dropdown__checkbox"
              containerClassName="checkbox-dropdown__label"
              disabled={props.disabled}
              indeterminate={isBulkCheckboxIndeterminate.current}
              label={!!props.customSelectAllText ? props.customSelectAllText : "Select All"}
              onChange={() => toggleSelectAll()}
              testId={`search-checkbox-dropdown__${props.testId}-select-all`}
            />
          }
          {groupedByGrandparentTitle()}
        </div>
      )}
    </div>
  );
};

interface ChildListProps {
  disabled: boolean;
  hideParentCheckbox?: boolean;
  keepParentOpen?: boolean;
  maxSelectedChildren?: number;
  onDropdownItemMouseOver?: (dropdownItemKey?: string) => void;
  onParentMouseOver?: (parentKey?: string) => void;
  parent: NestingParentDropdownItem;
  parentAsValue?: boolean;
  parentMatchesSearch: boolean;
  searchText: string;
  selectedLookup: Record<string, boolean>;
  showTooltipOnOverflow?: boolean;
  updateSelectedItems: (parentKey: string, childKey: string, isChecked: boolean) => void;
}

const ParentAndChildren: FC<ChildListProps> = (props: ChildListProps) => {
  const childListRef = useRef<HTMLDivElement>(null);
  const chevronRef = useRef<HTMLElement>(null);
  const { parent, selectedLookup, updateSelectedItems } = props;
  const [expanded, setExpanded] = useState(false);

  useEffect(() => {
    if (!props.parent.hideParent) {
      if (!!props.searchText && !props.parentMatchesSearch && !expanded) {
        handleOpen();
      }

      if (!!props.searchText && props.parentMatchesSearch && expanded) {
        handleClose();
      }

      if (!props.searchText && expanded) {
        handleClose();
      }
    }
  }, [props.searchText, props.parentMatchesSearch]);

  const handleOpen = () => {
    chevronRef.current?.classList.add("flip");
    setExpanded(true);
  };

  const handleClose = () => {
    chevronRef.current?.classList.remove("flip");
    setExpanded(false);
  };

  let parentCheckboxStatus: CheckboxStateEnum;
  let numberOfItemsSelected = 0;
  const parentSelectableChildren = selectableChildren(parent);
  parentSelectableChildren.forEach((child) => {
    if (selectedLookup[child.key]) {
      numberOfItemsSelected += 1;
    }
  })

  if (props.parentAsValue && parent.children.length === 0) {
    if (selectedLookup[parent.key]) {
      parentCheckboxStatus = CheckboxStateEnum.Checked;
    } else {
      parentCheckboxStatus = CheckboxStateEnum.Unchecked;
    }
  }
  else {
    if (numberOfItemsSelected >= parentSelectableChildren.length) {
      parentCheckboxStatus = CheckboxStateEnum.Checked;
    } else if (numberOfItemsSelected > 0) {
      parentCheckboxStatus = CheckboxStateEnum.Indeterminate;
    } else {
      parentCheckboxStatus = CheckboxStateEnum.Unchecked;
    }
  }

  const selectedChildrenCount = Object.values(selectedLookup).filter((v) => v).length;

  const getParentCheckbox = (label: string) => {
    return (
      <QESCheckbox
        label={label}
        checked={parentCheckboxStatus === CheckboxStateEnum.Checked}
        indeterminate={parentCheckboxStatus === CheckboxStateEnum.Indeterminate}
        testId={`multiselect-nested-dropdown__parent--${parent.key}`}
        onChange={(e) => updateSelectedItems(parent.key, undefined, (e.target as HTMLInputElement).checked)}
        onLabelClick={(e) => e.stopPropagation()}
        onClick={(e) => e.stopPropagation()}
        disabled={props.disabled || (selectedChildrenCount >= props.maxSelectedChildren && !parent.children.some((child) => selectedLookup[child.key]))}
        showTooltipOnOverflow={props.showTooltipOnOverflow}
      />
    );
  }

  return (
    <>
      {!props.parent.hideParent && (
        <div
          onClick={() => {
            if (!props.keepParentOpen && parent.children.length > 0) {
              expandCollapse(childListRef, handleClose, handleOpen);
            }
          }}
          onMouseEnter={!!props.onDropdownItemMouseOver ? (() => props.onParentMouseOver(props.parent.key)) : null}
          onMouseLeave={!!props.onDropdownItemMouseOver ? (() => props.onParentMouseOver()) : null}
          className="multiselect-nested-dropdown__expander"
        >
          {props.hideParentCheckbox ? (
            <span className="qes-checkbox__container">{parent.name}</span>
          ) : getParentCheckbox(parent.name)}
          {!props.keepParentOpen && parent.children.length > 0 && (
            <span className="expand-indicator" ref={chevronRef}><QesIcon iconName={QesIcons.ChevronDown} description={expanded ? "Collapse" : "Expand"} width="16" color={QesColors.QesPrimaryGray8} /></span>
          )}
        </div>
      )}
      {parent.children.length > 0 && (
        <div ref={childListRef} className={`${(props.keepParentOpen || props.parent.hideParent || (!!props.searchText && !props.parentMatchesSearch)) ? 'qes-js-open' : 'qes-js-closed'}`}>
          <ul className={cx({
            "parent-ul": props.parent.hideParent,
          })}>
            {parent.includeSelectAllAsOption && getParentCheckbox("Select All")}
            {parent.children.map((child) => {
              if (child.isDivider) {
                return (
                  <li key={child.key} className="multiselect-nested-dropdown__section-divider">
                    {child.name}
                  </li>
                );
              }
              return (
                <li
                  key={child.key}
                  onMouseEnter={!!props.onDropdownItemMouseOver ? (() => props.onDropdownItemMouseOver(child.key)) : null}
                  onMouseLeave={!!props.onDropdownItemMouseOver ? (() => props.onDropdownItemMouseOver()) : null}
                >
                  <QESCheckbox
                    label={child.display ?? child.name}
                    checked={selectedLookup[child.key]}
                    testId={`multiselect-nested-dropdown__child--${parent.key}:${child.key}`}
                    onChange={(e) => updateSelectedItems(parent.key, child.key, (e.target as HTMLInputElement).checked)}
                    disabled={props.disabled || child.disabled || ((selectedChildrenCount === props.maxSelectedChildren && !selectedLookup[child.key]) || selectedChildrenCount > props.maxSelectedChildren)}
                    showTooltipOnOverflow={props.showTooltipOnOverflow}
                  />
                </li>
              );
            })}
          </ul>
        </div>
      )}
    </>
  );
};

interface GrandparentProps {
  disabled: boolean;
  grandparentTitle: string;
  grouped: NestingParentDropdownItem[];
  hideExpanderIcon?: boolean;
  hideParentCheckbox?: boolean;
  keepParentOpen?: boolean;
  maxSelectedChildren?: number;
  onDropdownItemMouseOver?: (dropdownItemKey?: string) => void;
  onParentMouseOver?: (parentKey?: string) => void;
  parentAsValue?: boolean;
  parentMatchKeys: Set<string>;
  searchText: string;
  selectedLookup: Record<string, boolean>;
  showTooltipOnOverflow?: boolean;
  updateSelectedItems: (parentKey: string, childKey: string, isChecked: boolean) => void;
}

const Grandparent: FC<GrandparentProps> = (props: GrandparentProps) => {
  const [grandparentExpanderState, setGrandparentExpanderState] = useState<QesIcons>(QesIcons.PlusSquare);
  const [expanded, setExpanded] = useState(false);
  const childListRef2 = useRef<HTMLDivElement>(null);
  const chevronRef2 = useRef<HTMLElement>(null);
  const { selectedLookup, updateSelectedItems } = props;
  const uniqueId = useId();

  useEffect(() => {
    if (!!props.searchText && !expanded) {
      handleOpen();
    }

    if (!props.searchText && expanded) {
      handleClose();
    }
  }, [props.searchText]);

  const handleOpen = () => {
    chevronRef2.current?.classList.add("flip");
    setGrandparentExpanderState(QesIcons.MinusSquare);
    setExpanded(true);
  };

  const handleClose = () => {
    chevronRef2.current?.classList.remove("flip");
    setGrandparentExpanderState(QesIcons.PlusSquare);
    setExpanded(false);
  };

  return (
    <>
      <ul>
        <li>
          <>
            <div
              onClick={() => {
                if (!props.keepParentOpen && props.grouped.length > 0) {
                  expandCollapse(childListRef2, handleClose, handleOpen);
                }
              }}
              className="multiselect-nested-dropdown__expander"
            >
              <span className="grandparent-expander">
                {!props.hideExpanderIcon && (
                  <QesIcon iconName={grandparentExpanderState} width="20" description={grandparentExpanderState === QesIcons.PlusSquare ? "Expand section" : "Collapse section"} />
                )}
                <span className="qes-checkbox__container">{props.grandparentTitle}</span>
              </span>

              {!props.keepParentOpen && props.grouped.length > 0 && (
                <span className="expand-indicator" ref={chevronRef2}><QesIcon iconName={QesIcons.ChevronDown} description={expanded ? "Collaspe" : "Expand"} width="16" color={QesColors.QesPrimaryGray8} /></span>
              )}
            </div>
            <div ref={childListRef2} className={`${(props.keepParentOpen || !!props.searchText) ? 'qes-js-open' : 'qes-js-closed'}`}>
              <ul key={`${uniqueId}`} className="grandparent-ul">
                {props.grouped.map((parent) => {
                  return (
                    <li key={parent.key}>
                      <ParentAndChildren
                        disabled={props.disabled}
                        hideParentCheckbox={props.hideParentCheckbox}
                        parent={parent}
                        parentMatchesSearch={props.parentMatchKeys.has(parent.key)}
                        updateSelectedItems={updateSelectedItems}
                        searchText={props.searchText}
                        selectedLookup={selectedLookup}
                        showTooltipOnOverflow={props.showTooltipOnOverflow}
                        onDropdownItemMouseOver={props.onDropdownItemMouseOver}
                        onParentMouseOver={props.onParentMouseOver}
                        parentAsValue={props.parentAsValue}
                        keepParentOpen={props.keepParentOpen}
                        maxSelectedChildren={props.maxSelectedChildren}
                      />
                    </li>
                  )
                })}
              </ul>
            </div>

          </>
        </li>
      </ul>
    </>
  );
};

export default MultiselectNestedDropdown;
